import { TSatisfyClientToServerEvents } from '@/websockets/contexts/socket.context';
import { IUseSocketReturn } from '@/websockets/hooks/use-socket';
import { useCallback, useEffect, useRef } from 'react';
import { queryClient } from '@/lib/react-query';
import { spaceUpdateWebsocketStore } from './space-update-websocket.store';
import {
  GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY,
  TGetListArchiveTicketBySpaceResponse,
} from '@/features/ticketings/tickets/ticket/api/get-archive-ticket-by-space.api';
import {
  GET_LIST_TICKET_BY_SPACE_QUERY_KEY,
  TGetListTicketBySpaceResponse,
} from '@/features/ticketings/tickets/ticket/api/get-ticket-by-space.api';
import { TTicket, TTicketBySpace } from '@/features/ticketings/tickets/ticket/types/ticket.type';
import { sortTicketsByPosition } from '@/features/ticketings/tickets/ticket/utils/ordering-ticket.utils';
import { sortColumnsByPosition } from '@/features/ticketings/columns/column/utils/ordering-column.utils';
import { TColumn } from '../../columns/column/types/column.type';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_COLUMN_COLOR = '#000000';

// Increased timeout to ensure backend has fully processed all tickets
// This prevents stale data when querying immediately after archive/restore
const COLUMN_ARCHIVE_RESTORE_TIMEOUT = 1000;
// ============================================================================
// TIMEOUT MANAGEMENT
// ============================================================================

/**
 * Map to track pending timeouts for query invalidation per spaceId
 * Key: spaceId, Value: timeout ID
 */
const columnArchiveRestoreTimeouts = new Map<string, NodeJS.Timeout>();

/**
 * Clear existing timeout for a spaceId if it exists
 */
const clearColumnArchiveRestoreTimeout = (spaceId: string) => {
  const existingTimeout = columnArchiveRestoreTimeouts.get(spaceId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    columnArchiveRestoreTimeouts.delete(spaceId);
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Creates a deep copy of the query data payload
 */
const deepCopyPayload = (payload: TTicketBySpace[]): TTicketBySpace[] => {
  return payload.map((col) => ({
    ...col,
    tickets: [...(col.tickets ?? [])],
  }));
};

/**
 * Normalizes ticket data with default values for TTicketBySpace['tickets'] format
 */
const normalizeTicketData = (ticketData: TTicket): TTicketBySpace['tickets'][number] => {
  return {
    ticketId: ticketData.ticketId,
    title: ticketData.title,
    startDate: ticketData.startDate ?? null,
    endDate: ticketData.endDate ?? null,
    position: ticketData.position ?? null,
    isCompleted: ticketData.isCompleted ?? false,
    createdAt: ticketData.createdAt ?? '',
    ticketLabels: ticketData.ticketLabels ?? [],
    users: ticketData.users ?? [],
    creator: ticketData.creator ?? null,
    totalComments: ticketData.totalComments ?? 0,
    totalAttachments: ticketData.totalAttachments ?? 0,
  };
};

/**
 * Sorts columns by their columnIndex
 */
const sortColumnsByIndex = (columns: TTicketBySpace[]): void => {
  columns.sort((a, b) => {
    if (!a.columnIndex && !b.columnIndex) return 0;
    if (!a.columnIndex) return 1;
    if (!b.columnIndex) return -1;
    return a.columnIndex < b.columnIndex ? -1 : a.columnIndex > b.columnIndex ? 1 : 0;
  });
};

// ============================================================================
// SOCKET SYNC HANDLERS
// ============================================================================

// TICKET EVENTS
const handleTicketCreated = (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !spaceColumnId || !data || !('ticketId' in data)) {
    return;
  }

  const ticketData = data as TTicket;
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);
    const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIdx >= 0) {
      const targetColumn = newPayload[targetColumnIdx];
      const existingTicketIndex = targetColumn.tickets.findIndex((t) => t.ticketId === ticketData.ticketId);

      if (existingTicketIndex >= 0) {
        // Update existing ticket
        targetColumn.tickets[existingTicketIndex] = normalizeTicketData(ticketData);
      } else {
        // Add new ticket
        targetColumn.tickets.push(normalizeTicketData(ticketData));
      }

      newPayload[targetColumnIdx].tickets = sortTicketsByPosition(targetColumn.tickets);
    }

    return {
      ...old,
      payload: newPayload,
    };
  });
};

const handleTicketUpdated = (payload: TSpaceUpdatePayload) => {
  const { spaceId, ticketId, newSpaceColumnId, oldSpaceColumnId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !ticketId || !data || !('ticketId' in data)) {
    return;
  }

  const ticketData = data as TTicket;
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);
    const targetColumnId = newSpaceColumnId || spaceColumnId;
    const targetColumnIdx = targetColumnId ? newPayload.findIndex((col) => col.spaceColumnId === targetColumnId) : -1;
    const sourceColumnIdx = oldSpaceColumnId
      ? newPayload.findIndex((col) => col.spaceColumnId === oldSpaceColumnId)
      : -1;

    // handleSocketTicketUpdated(newPayload, ticketData, ticketId, sourceColumnIdx, targetColumnIdx);

    const isMovingBetweenColumns = sourceColumnIdx >= 0 && targetColumnIdx >= 0 && sourceColumnIdx !== targetColumnIdx;

    if (isMovingBetweenColumns) {
      // Move ticket between columns
      const sourceTicketIndex = newPayload[sourceColumnIdx].tickets.findIndex((t) => t.ticketId === ticketId);
      if (sourceTicketIndex >= 0) {
        const [movedTicket] = newPayload[sourceColumnIdx].tickets.splice(sourceTicketIndex, 1);
        const updatedTicket: TTicketBySpace['tickets'][number] = {
          ...movedTicket,
          ...normalizeTicketData(ticketData),
        };

        const targetColumn = newPayload[targetColumnIdx];
        const existingTicketIndex = targetColumn.tickets.findIndex((t) => t.ticketId === ticketId);

        if (existingTicketIndex >= 0) {
          targetColumn.tickets[existingTicketIndex] = updatedTicket;
        } else {
          targetColumn.tickets.push(updatedTicket);
        }

        newPayload[targetColumnIdx].tickets = sortTicketsByPosition(targetColumn.tickets);
      }
    } else if (targetColumnIdx >= 0) {
      // Update ticket in same column
      const targetColumn = newPayload[targetColumnIdx];
      const ticketIndex = targetColumn.tickets.findIndex((t) => t.ticketId === ticketId);
      if (ticketIndex >= 0) {
        targetColumn.tickets[ticketIndex] = normalizeTicketData(ticketData);
        newPayload[targetColumnIdx].tickets = sortTicketsByPosition(targetColumn.tickets);
      }
    }

    return {
      ...old,
      payload: newPayload,
    };
  });
};

const handleTicketDeleted = (payload: TSpaceUpdatePayload) => {
  const { spaceId, ticketId, spaceColumnId } = payload;

  // Validate required data
  if (!spaceId || !ticketId || !spaceColumnId) {
    return;
  }

  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);
    const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIdx >= 0) {
      newPayload[targetColumnIdx].tickets = newPayload[targetColumnIdx].tickets.filter((t) => t.ticketId !== ticketId);
    }

    return {
      ...old,
      payload: newPayload,
    };
  });
};

const handleTicketArchived = (payload: TSpaceUpdatePayload) => {
  const { spaceId, ticketId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !ticketId || !spaceColumnId) {
    return;
  }

  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];
  const archiveQueryKey = [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  // Get active data to extract ticket and column info before updating
  const activeData = queryClient.getQueryData<TGetListTicketBySpaceResponse>(activeQueryKey);
  const activeColumn = activeData?.payload?.find((col) => col.spaceColumnId === spaceColumnId);
  const activeTicket = activeColumn?.tickets?.find((t) => t.ticketId === ticketId);

  // Use ticket data from payload if available, otherwise use from active list
  const ticketData = (data && 'ticketId' in data ? data : activeTicket) as TTicket | undefined;

  if (!ticketData) {
    // If we don't have ticket data, just remove from active list
    queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
      if (!old?.payload) return old;

      const newPayload = deepCopyPayload(old.payload);
      const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

      if (targetColumnIdx >= 0) {
        newPayload[targetColumnIdx].tickets = newPayload[targetColumnIdx].tickets.filter(
          (t) => t.ticketId !== ticketId
        );
      }

      return {
        ...old,
        payload: newPayload,
      };
    });
    return;
  }

  // Normalize ticket data for archive list format (preserves position from active list)
  const normalizedTicket: TTicketBySpace['tickets'][number] = normalizeTicketData(ticketData);

  // Remove ticket from active list
  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    // Create a deep copy of the payload to ensure new references
    const newPayload = deepCopyPayload(old.payload);
    const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIdx >= 0) {
      newPayload[targetColumnIdx].tickets = newPayload[targetColumnIdx].tickets.filter((t) => t.ticketId !== ticketId);
    }

    return {
      ...old,
      payload: newPayload,
    };
  });

  // Add ticket to archived list
  queryClient.setQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey, (old) => {
    if (!old?.payload) {
      // If archive list doesn't exist, create it with the ticket
      return {
        payload: [
          {
            spaceColumnId: spaceColumnId,
            name: activeColumn?.name || '',
            columnIndex: activeColumn?.columnIndex || '0',
            color: activeColumn?.color || DEFAULT_COLUMN_COLOR,
            tickets: [normalizedTicket],
          },
        ],
      } as TGetListArchiveTicketBySpaceResponse;
    }

    // Create a deep copy of the payload to ensure new references
    const newPayload = deepCopyPayload(old.payload);

    // Find the target column in archive list
    const targetColumnIndex = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIndex >= 0) {
      // Column exists, add ticket to it
      const targetColumn = newPayload[targetColumnIndex];
      const existingTicketIndex = targetColumn.tickets?.findIndex((t) => t.ticketId === ticketId) ?? -1;

      if (existingTicketIndex >= 0) {
        // Update existing ticket (preserves position)
        targetColumn.tickets[existingTicketIndex] = normalizedTicket;
      } else {
        // Add new ticket
        targetColumn.tickets = [...(targetColumn.tickets || []), normalizedTicket];
      }

      // Sort tickets by position to maintain consistent ordering
      targetColumn.tickets = sortTicketsByPosition(targetColumn.tickets);

      // Create new column reference
      newPayload[targetColumnIndex] = {
        ...targetColumn,
        tickets: [...targetColumn.tickets],
      };

      return {
        ...old,
        payload: newPayload,
      };
    } else {
      // Column doesn't exist in archive list, create it with the ticket
      return {
        ...old,
        payload: [
          ...newPayload,
          {
            spaceColumnId: spaceColumnId,
            name: activeColumn?.name || '',
            columnIndex: activeColumn?.columnIndex || '0',
            color: activeColumn?.color || DEFAULT_COLUMN_COLOR,
            tickets: [normalizedTicket],
          },
        ],
      };
    }
  });
};

const handleTicketRestored = (payload: TSpaceUpdatePayload) => {
  const { spaceId, ticketId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !ticketId || !spaceColumnId || !data || !('ticketId' in data)) {
    return;
  }

  const ticketData = data as TTicket;
  const archiveQueryKey = [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  // Get archive data to extract ticket and column info before updating
  const archiveData = queryClient.getQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey);
  const archiveColumn = archiveData?.payload?.find((col) => col.spaceColumnId === spaceColumnId);
  const archiveTicket = archiveColumn?.tickets?.find((t) => t.ticketId === ticketId);

  // Use ticket data from payload if available, otherwise use from archive list
  // This ensures we preserve the position from the archive list
  const ticketToRestore = ticketData || archiveTicket;

  if (!ticketToRestore) {
    // If we don't have ticket data, just remove from archive list
    queryClient.setQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey, (old) => {
      if (!old?.payload) return old;

      const newPayload = deepCopyPayload(old.payload);
      const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

      if (targetColumnIdx >= 0) {
        newPayload[targetColumnIdx].tickets = newPayload[targetColumnIdx].tickets.filter(
          (t) => t.ticketId !== ticketId
        );
      }

      return {
        ...old,
        payload: newPayload,
      };
    });
    return;
  }

  // Normalize ticket data for active list format (preserves position)
  const normalizedTicket: TTicketBySpace['tickets'][number] = normalizeTicketData(ticketToRestore);

  // Remove ticket from archive list
  queryClient.setQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey, (old) => {
    if (!old?.payload) return old;

    // Create a deep copy of the payload to ensure new references
    const newPayload = deepCopyPayload(old.payload);
    const targetColumnIdx = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIdx >= 0) {
      newPayload[targetColumnIdx].tickets = newPayload[targetColumnIdx].tickets.filter((t) => t.ticketId !== ticketId);
    }

    return {
      ...old,
      payload: newPayload,
    };
  });

  // Add ticket back to active list
  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) {
      // If active list doesn't exist, create it with the ticket
      return {
        payload: [
          {
            spaceColumnId: spaceColumnId,
            name: archiveColumn?.name || '',
            columnIndex: archiveColumn?.columnIndex || '0',
            color: archiveColumn?.color || DEFAULT_COLUMN_COLOR,
            tickets: [normalizedTicket],
          },
        ],
      } as TGetListTicketBySpaceResponse;
    }

    // Create a deep copy of the payload to ensure new references
    const newPayload = deepCopyPayload(old.payload);

    // Find the target column in active list
    const targetColumnIndex = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (targetColumnIndex >= 0) {
      // Column exists, add ticket to it
      const targetColumn = newPayload[targetColumnIndex];
      const existingTicketIndex = targetColumn.tickets?.findIndex((t) => t.ticketId === ticketId) ?? -1;

      if (existingTicketIndex >= 0) {
        // Update existing ticket (preserves position)
        targetColumn.tickets[existingTicketIndex] = normalizedTicket;
      } else {
        // Add new ticket
        targetColumn.tickets = [...(targetColumn.tickets || []), normalizedTicket];
      }

      // Sort tickets by position to maintain consistent ordering
      targetColumn.tickets = sortTicketsByPosition(targetColumn.tickets);

      // Create new column reference
      newPayload[targetColumnIndex] = {
        ...targetColumn,
        tickets: [...targetColumn.tickets],
      };

      return {
        ...old,
        payload: newPayload,
      };
    } else {
      // Column doesn't exist in active list, create it with the ticket
      return {
        ...old,
        payload: [
          ...newPayload,
          {
            spaceColumnId: spaceColumnId,
            name: archiveColumn?.name || '',
            columnIndex: archiveColumn?.columnIndex || '0',
            color: archiveColumn?.color || DEFAULT_COLUMN_COLOR,
            tickets: [normalizedTicket],
          },
        ],
      };
    }
  });
};

// COLUMN EVENTS
const handleColumnCreated = (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !spaceColumnId || !data || !('spaceColumnId' in data)) {
    return;
  }

  const columnData = data as TColumn;
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);
    const existingColumnIndex = newPayload.findIndex((col) => col.spaceColumnId === columnData.spaceColumnId);

    if (existingColumnIndex >= 0) {
      // Update existing column
      newPayload[existingColumnIndex] = {
        ...newPayload[existingColumnIndex],
        name: columnData.name,
        columnIndex: columnData.columnIndex,
        color: columnData.color || newPayload[existingColumnIndex].color,
      };
    } else {
      // Add new column
      newPayload.push({
        spaceColumnId: columnData.spaceColumnId,
        name: columnData.name,
        columnIndex: columnData.columnIndex,
        color: columnData.color || DEFAULT_COLUMN_COLOR,
        tickets: [],
      });
    }

    sortColumnsByIndex(newPayload);

    return {
      ...old,
      payload: sortColumnsByPosition(newPayload),
    };
  });
};

const handleColumnUpdated = (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId, data } = payload;

  // Validate required data
  if (!spaceId || !spaceColumnId || !data || !('spaceColumnId' in data)) {
    return;
  }

  const columnData = data as TColumn;
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);
    const columnIndex = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (columnIndex >= 0) {
      newPayload[columnIndex] = {
        ...newPayload[columnIndex],
        ...columnData,
        color: columnData.color || newPayload[columnIndex].color,
      };
      sortColumnsByIndex(newPayload);
    }

    return {
      ...old,
      payload: sortColumnsByPosition(newPayload),
    };
  });
};

const handleColumnDeleted = (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId } = payload;

  // Validate required data
  if (!spaceId || !spaceColumnId) {
    return;
  }

  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];

  queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
    if (!old?.payload) return old;

    const newPayload = deepCopyPayload(old.payload);

    const columnIndex = newPayload.findIndex((col) => col.spaceColumnId === spaceColumnId);

    if (columnIndex >= 0) {
      newPayload.splice(columnIndex, 1);
    }

    return {
      ...old,
      payload: newPayload,
    };
  });
};

const handleColumnArchived = async (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId } = payload;

  if (!spaceId || !spaceColumnId) return;

  const { actions } = spaceUpdateWebsocketStore.getState();

  // Set archiving state for this column
  // actions.setColumnOperation(spaceColumnId, 'archiving');

  // Clear any existing timeout for this spaceId
  clearColumnArchiveRestoreTimeout(spaceId);

  // Wait before invalidating queries to ensure backend has fully processed all tickets
  // This prevents fetching stale data when the operation completes too quickly
  await new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        }),
      ]).finally(() => {
        // Clear the archiving state after query invalidation completes
        // This ensures the UI shows the correct state only after fresh data is fetched
        actions.clearColumnOperation(spaceColumnId);
        // Clean up the timeout from the map after execution
        columnArchiveRestoreTimeouts.delete(spaceId);
        resolve();
      });
    }, COLUMN_ARCHIVE_RESTORE_TIMEOUT);

    // Store the timeout ID for potential cleanup
    columnArchiveRestoreTimeouts.set(spaceId, timeoutId);
  });
};

const handleColumnRestored = async (payload: TSpaceUpdatePayload) => {
  const { spaceId, spaceColumnId } = payload;

  if (!spaceId || !spaceColumnId) return;

  const { actions } = spaceUpdateWebsocketStore.getState();

  // Set restoring state for this column
  // actions.setColumnOperation(spaceColumnId, 'restoring');

  // Clear any existing timeout for this spaceId
  clearColumnArchiveRestoreTimeout(spaceId);

  // Wait before invalidating queries to ensure backend has fully processed all tickets
  // This prevents fetching stale data when the operation completes too quickly
  await new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        }),
      ]).finally(() => {
        // Clear the restoring state after query invalidation completes
        // This ensures the UI shows the correct state only after fresh data is fetched
        actions.clearColumnOperation(spaceColumnId);
        // Clean up the timeout from the map after execution
        columnArchiveRestoreTimeouts.delete(spaceId);
        resolve();
      });
    }, COLUMN_ARCHIVE_RESTORE_TIMEOUT);

    // Store the timeout ID for potential cleanup
    columnArchiveRestoreTimeouts.set(spaceId, timeoutId);
  });
};

export type TSpaceTicketType =
  | 'ticket_updated'
  | 'ticket_created'
  | 'ticket_deleted'
  | 'ticket_archived'
  | 'ticket_restored';

export type TSpaceColumnType =
  | 'space_column_created'
  | 'space_column_updated'
  | 'space_column_deleted'
  | 'space_column_archived'
  | 'space_column_restored';

export type TSocketEventType =
  | 'TICKET_CREATED'
  | 'TICKET_UPDATED'
  | 'TICKET_DELETED'
  | 'TICKET_ARCHIVED'
  | 'TICKET_RESTORED'
  | 'COLUMN_CREATED'
  | 'COLUMN_UPDATED'
  | 'COLUMN_DELETED'
  | 'COLUMN_ARCHIVED'
  | 'COLUMN_RESTORED';

export const SOCKET_EVENT_TYPES: Record<TSocketEventType, TSpaceTicketType | TSpaceColumnType> = {
  TICKET_CREATED: 'ticket_created',
  TICKET_UPDATED: 'ticket_updated',
  TICKET_DELETED: 'ticket_deleted',
  TICKET_ARCHIVED: 'ticket_archived',
  TICKET_RESTORED: 'ticket_restored',
  COLUMN_CREATED: 'space_column_created',
  COLUMN_UPDATED: 'space_column_updated',
  COLUMN_DELETED: 'space_column_deleted',
  COLUMN_ARCHIVED: 'space_column_archived',
  COLUMN_RESTORED: 'space_column_restored',
};

export type TSpaceUpdatePayload = {
  type: TSpaceTicketType | TSpaceColumnType;
  spaceId: string;
  ticketId: string;
  spaceColumnId: string;
  oldSpaceColumnId: string;
  newSpaceColumnId: string;
  data?: TTicket | TColumn;
};

export type TSpaceUpdateServerToClientEvents = {
  space_update: (payload: TSpaceUpdatePayload) => void;
};

export function useSpaceUpdateSocketListeners(
  socket: IUseSocketReturn<TSpaceUpdateServerToClientEvents, TSatisfyClientToServerEvents>
) {
  // HOOKS
  const eventListenersAttached = useRef(false);

  // HANDLERS
  const handleSpaceUpdate = useCallback(async (payload: TSpaceUpdatePayload) => {
    const { type } = payload;

    switch (type) {
      // TICKET EVENTS
      case SOCKET_EVENT_TYPES.TICKET_CREATED:
        handleTicketCreated(payload);
        break;
      case SOCKET_EVENT_TYPES.TICKET_UPDATED:
        handleTicketUpdated(payload);
        break;
      case SOCKET_EVENT_TYPES.TICKET_DELETED:
        handleTicketDeleted(payload);
        break;
      case SOCKET_EVENT_TYPES.TICKET_ARCHIVED:
        handleTicketArchived(payload);
        break;
      case SOCKET_EVENT_TYPES.TICKET_RESTORED:
        handleTicketRestored(payload);
        break;

      // COLUMN EVENTS
      case SOCKET_EVENT_TYPES.COLUMN_CREATED:
        handleColumnCreated(payload);
        break;
      case SOCKET_EVENT_TYPES.COLUMN_UPDATED:
        handleColumnUpdated(payload);
        break;
      case SOCKET_EVENT_TYPES.COLUMN_DELETED:
        handleColumnDeleted(payload);
        break;
      case SOCKET_EVENT_TYPES.COLUMN_ARCHIVED:
        await handleColumnArchived(payload);
        break;
      case SOCKET_EVENT_TYPES.COLUMN_RESTORED:
        await handleColumnRestored(payload);
        break;
    }
  }, []);

  // EFFECTS
  useEffect(() => {
    if (!socket?.isConnected || eventListenersAttached.current) return;

    socket.on('space_update', handleSpaceUpdate);

    eventListenersAttached.current = true;

    return () => {
      socket.off('space_update', handleSpaceUpdate);
      eventListenersAttached.current = false;

      // Clean up all pending timeouts to prevent memory leaks
      columnArchiveRestoreTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      columnArchiveRestoreTimeouts.clear();

      // Clear all column operations on unmount
      const { actions } = spaceUpdateWebsocketStore.getState();
      actions.clearAllColumnOperations();
    };
  }, [socket, handleSpaceUpdate]);

  return null;
}
